import { Component, Inject, OnDestroy, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DrawingIssueSet } from '../../../../../../../../shared/models/drawing-sheet';
import { DrawingUploadEvent, ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { DrawingsAnalytics, errorProps, sizeMb } from '../drawings-analytics';

export interface DrawingUploadDialogData {
  projectId: string;
  /** W65: Upload new revision from a sheet: the set's page 1 replaces this sheet ("New revision of 4 · PROJECT LAYOUT") */
  sheet?: { uuid: string; number: string; label: string } | null;
}

@Component({
  selector: 'app-drawing-upload-dialog',
  templateUrl: './drawing-upload-dialog.component.html',
  styleUrls: ['./drawing-upload-dialog.component.scss'],
  standalone: false,
})
export class DrawingUploadDialogComponent implements OnDestroy {
  file: File | null = null;
  form = new FormGroup({
    name: new FormControl('', [Validators.maxLength(256)]),
    // app-date-picker holds a moment; the API takes YYYY-MM-DD
    issuedAt: new FormControl<moment.Moment | string | null>(null),
  });
  error = '';
  submitting = false;
  dragging = false;
  /** Where a running upload is: hashing, the PUT, or finalizing (null when idle) */
  progress: DrawingUploadEvent | null = null;
  private readonly track = inject(DrawingsAnalytics);
  private upload: Subscription | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DrawingUploadDialogData,
    private ref: MatDialogRef<DrawingUploadDialogComponent, DrawingIssueSet | null>,
    private api: ProjectDrawingsService,
  ) {
    // W65: a new revision of one sheet is named for it unless the user names it
    if (data.sheet) {
      this.form.controls.name.setValue(`${data.sheet.number} · new revision`);
    }
  }

  pick(files: FileList | null): void {
    // A file dropped mid-upload is not picked
    if (this.submitting) { return; }
    const f = files?.[0] ?? null;
    if (f && f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      this.error = 'Only PDF files can be uploaded as drawings.';
      this.file = null;
      return;
    }
    this.error = '';
    this.file = f;
    const name = this.form.controls.name;
    if (f && !name.value) {
      name.setValue(f.name.replace(/\.pdf$/i, ''));
    }
  }

  drop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    this.pick(event.dataTransfer?.files ?? null);
  }

  over(event: DragEvent): void {
    event.preventDefault();
    this.dragging = true;
  }

  submit(): void {
    if (!this.file || this.submitting || this.form.invalid) { return; }
    this.submitting = true;
    // Review I3: no backdrop or Esc close mid-upload, or the register would never open the new set's review
    this.ref.disableClose = true;
    this.error = '';
    const { name, issuedAt } = this.form.value;
    const issued = issuedAt && moment(issuedAt).isValid() ? moment(issuedAt).format('YYYY-MM-DD') : null;
    const size_mb = sizeMb(this.file.size);
    const started = Date.now();
    this.track.track('set_upload_started', { size_mb });
    if (this.data.sheet) {
      this.track.track('revision_upload_started', { sheet_uuid: this.data.sheet.uuid });
    }
    // Submit again after a failure resumes: the intent hands back the same set with a fresh grant
    this.upload = this.api
      .uploadSetDirect(this.data.projectId, this.file, { name: name || this.file.name, issuedAt: issued, sheetUuid: this.data.sheet?.uuid })
      .subscribe({
        next: event => {
          if (event.stage !== 'done') {
            this.progress = event;
            return;
          }
          this.track.track('set_upload_succeeded', { set_uuid: event.set?.uuid, size_mb, duration_ms: Date.now() - started });
          this.ref.close(event.set);
        },
        error: e => {
          this.track.track('set_upload_failed', { ...errorProps(e), size_mb });
          this.submitting = false;
          this.progress = null;
          this.ref.disableClose = false;
          this.error = this.api.uploadFailure(e);
        },
      });
  }

  /** "Preparing file… 40%", "Uploading 45 of 132 MB", "Starting…" */
  get stageLabel(): string {
    const p = this.progress;
    switch (p?.stage) {
      case 'hashing':
        return `Preparing file… ${this.percent}%`;
      case 'uploading':
        return `Uploading ${megabytes(p.loaded)} of ${megabytes(p.total)} MB`;
      case 'finalizing':
        return 'Starting…';
      default:
        return '';
    }
  }

  /** The bar's value: hashing and uploading have sizes; finalizing has none (the bar runs indeterminate) */
  get percent(): number {
    const p = this.progress;
    if (p?.stage !== 'hashing' && p?.stage !== 'uploading') {
      return 0;
    }
    return p.total ? Math.min(100, Math.floor((p.loaded / p.total) * 100)) : 0;
  }

  ngOnDestroy(): void {
    this.upload?.unsubscribe();
  }

  removeFile(): void {
    if (this.submitting) { return; }
    this.file = null;
    this.error = '';
  }
}

/** Whole megabytes, or one decimal under 10 MB so a small file still moves ("0.4 of 2.1 MB") */
function megabytes(bytes: number): string {
  const mb = (bytes ?? 0) / (1024 * 1024);
  return mb < 10 ? (Math.floor(mb * 10) / 10).toFixed(1) : String(Math.floor(mb));
}
